/**
 * Permission Management System for Qualia Frontend
 * Integrates with backend login API response to manage user permissions and roles
 */

class PermissionManager {
  constructor() {
    this.user = null;
    this.permissions = [];
    this.role = null;
    this.init();
  }

  /**
   * Initialize permission manager with stored user data
   */
  init() {
    this.loadUserFromStorage();
  }

  /**
   * Load user data from localStorage
   */
  loadUserFromStorage() {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        this.setUser(userData);
      }
    } catch (error) {
      console.error('Error loading user from storage:', error);
      this.clearUser();
    }
  }

  /**
   * Set user data and extract permissions
   * @param {Object} userData - User data from login API response
   */
  setUser(userData) {
    this.user = userData;
    this.role = userData?.role || null;
    this.permissions = userData?.role?.permissions?.permissions || [];
    
    console.log(this.permissions)
    console.log({userData})
    // Store in localStorage
    localStorage.setItem('user', JSON.stringify(userData));
  }

  /**
   * Clear user data and permissions
   */
  clearUser() {
    this.user = null;
    this.role = null;
    this.permissions = [];
    localStorage.removeItem('user');
  }

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean}
   */
  hasPermission(permission) {
    if (!this.permissions || !Array.isArray(this.permissions)) {
      return false;
    }
    return this.permissions.includes(permission);
  }

  /**
   * Check if user has any of the specified permissions
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean}
   */
  hasAnyPermission(permissions) {
    if (!Array.isArray(permissions)) {
      return false;
    }
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Check if user has all of the specified permissions
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean}
   */
  hasAllPermissions(permissions) {
    if (!Array.isArray(permissions)) {
      return false;
    }
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * Check if user has a specific role
   * @param {string} roleName - Role name to check
   * @returns {boolean}
   */
  hasRole(roleName) {
    return this.role?.name === roleName;
  }

  /**
   * Check if user has any of the specified roles
   * @param {string[]} roles - Array of role names to check
   * @returns {boolean}
   */
  hasAnyRole(roles) {
    if (!Array.isArray(roles)) {
      return false;
    }
    return roles.some(role => this.hasRole(role));
  }

  /**
   * Get user's role name
   * @returns {string|null}
   */
  getRoleName() {
    return this.role?.name || null;
  }

  /**
   * Get user's role display name
   * @returns {string|null}
   */
  getRoleDisplayName() {
    const roleDisplayNames = {
      'super_admin': 'Super Admin',
      'manager': 'Manager',
      'team': 'Team Member'
    };
    return roleDisplayNames[this.getRoleName()] || this.getRoleName();
  }

  /**
   * Get all user permissions
   * @returns {string[]}
   */
  getPermissions() {
    return [...this.permissions];
  }

  /**
   * Get user information
   * @returns {Object|null}
   */
  getUser() {
    return this.user;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!this.user && !!localStorage.getItem('access_token');
  }

  /**
   * Check if user is super admin
   * @returns {boolean}
   */
  isSuperAdmin() {
    return this.hasRole('super_admin');
  }

  /**
   * Check if user is manager or above
   * @returns {boolean}
   */
  isManagerOrAbove() {
    return this.hasAnyRole(['super_admin', 'manager']);
  }

  /**
   * Check if user can access a specific route
   * @param {string} route - Route path
   * @returns {boolean}
   */
  canAccessRoute(route) {
    // Define route permissions mapping
    const routePermissions = {
      '/dashboard': ['view_dashboard'],
      '/automation-runs': ['view_automation_runs'],
      '/automation/logs': ['view_logs'],
      '/users': ['view_users', 'manage_users'],
      '/settings': ['manage_settings'],
      // Filtered automation routes
      '/automation/both-agents-present': ['view_automation_runs'],
      '/automation/listing-agents-only': ['view_automation_runs'],
      '/automation/selling-agents-only': ['view_automation_runs'],
      '/automation/fsbo-no-agents': ['view_automation_runs'],
      '/automation/agent-copy-missing': ['view_automation_runs'],
      '/automation/both-agents-missing': ['view_automation_runs'],
      '/automation/no-completion-status': ['view_automation_runs']
    };

    const requiredPermissions = routePermissions[route];
    if (!requiredPermissions) {
      // If no specific permissions defined, allow access for authenticated users
      return this.isAuthenticated();
    }

    return this.hasAnyPermission(requiredPermissions);
  }

  /**
   * Get navigation items based on user permissions
   * @returns {Array}
   */
  getNavigationItems() {
    const allNavItems = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        path: '/dashboard',
        icon: 'fas fa-fw fa-tachometer-alt',
        requiredPermissions: ['view_dashboard']
      },
      {
        id: 'automation-runs',
        label: 'Automation Runs',
        path: '/automation-runs',
        icon: 'fas fa-fw fa-cogs',
        requiredPermissions: ['view_automation_runs']
      },
      {
        id: 'scheduled-appointments',
        label: 'Scheduled Appointments',
        path: '/scheduled-appointments',
        icon: 'fas fa-fw fa-calendar-alt',
        requiredPermissions: ['view_dashboard']
      },
      {
        id: 'commitments',
        label: 'Commitments',
        path: '/commitments',
        icon: 'fas fa-fw fa-handshake',
        requiredPermissions: ['view_dashboard']
      },
      {
        id: 'automation-filters',
        label: 'Automation Filters',
        icon: 'fas fa-fw fa-filter',
        requiredPermissions: ['view_automation_runs'],
        children: [
          {
            id: 'both-agents-present',
            label: 'Both Agents Present',
            path: '/automation/both-agents-present'
          },
          {
            id: 'listing-agents-only',
            label: 'Listing Agents Only',
            path: '/automation/listing-agents-only'
          },
          {
            id: 'selling-agents-only',
            label: 'Selling Agents Only',
            path: '/automation/selling-agents-only'
          },
          {
            id: 'fsbo-no-agents',
            label: 'FSBO No Agents',
            path: '/automation/fsbo-no-agents'
          },
          {
            id: 'agent-copy-missing',
            label: 'Agent Copy Missing',
            path: '/automation/agent-copy-missing'
          },
          {
            id: 'both-agents-missing',
            label: 'Both Agents Missing',
            path: '/automation/both-agents-missing'
          },
          {
            id: 'no-completion-status',
            label: 'No Completion Status',
            path: '/automation/no-completion-status'
          }
        ]
      },
      {
        id: 'users',
        label: 'User Management',
        path: '/users',
        icon: 'fas fa-fw fa-users',
        requiredPermissions: ['view_users', 'manage_users']
      },
      {
        id: 'settings',
        label: 'Settings',
        path: '/settings',
        icon: 'fas fa-fw fa-cog',
        requiredPermissions: ['manage_settings']
      }
    ];

    // Filter navigation items based on user permissions
    return allNavItems.filter(item => {
      if (item.requiredPermissions) {
        return this.hasAnyPermission(item.requiredPermissions);
      }
      return true;
    });
  }
}

// Create singleton instance
const permissionManager = new PermissionManager();

// Export utility functions for easy use in components
export const hasPermission = (permission) => permissionManager.hasPermission(permission);
export const hasAnyPermission = (permissions) => permissionManager.hasAnyPermission(permissions);
export const hasAllPermissions = (permissions) => permissionManager.hasAllPermissions(permissions);
export const hasRole = (roleName) => permissionManager.hasRole(roleName);
export const hasAnyRole = (roles) => permissionManager.hasAnyRole(roles);
export const getRoleName = () => permissionManager.getRoleName();
export const getRoleDisplayName = () => permissionManager.getRoleDisplayName();
export const getPermissions = () => permissionManager.getPermissions();
export const getUser = () => permissionManager.getUser();
export const isAuthenticated = () => permissionManager.isAuthenticated();
export const isSuperAdmin = () => permissionManager.isSuperAdmin();
export const isManagerOrAbove = () => permissionManager.isManagerOrAbove();
export const canAccessRoute = (route) => permissionManager.canAccessRoute(route);
export const getNavigationItems = () => permissionManager.getNavigationItems();

// Export the manager instance for advanced usage
export default permissionManager;